import { sectionRenderer, buildSectionSelector, normalizeSectionId } from '@theme/section-renderer';
import { withViewTransition } from '@theme/kb-view-transition';

/**
 * @typedef {object} KbSectionReplacement
 * @property {string} sectionId
 * @property {Element} current
 * @property {Element} next
 */

/**
 * @typedef {object} KbSwapOptions
 * @property {boolean} push
 */

let popstateBound = false;

const renderedSectionHTML = new Map();

async function fetchSectionHTML(sectionIds, url, useCache) {
  const entries = await Promise.all(
    sectionIds.map(async (sectionId) => [
      sectionId,
      await sectionRenderer.getSectionHTML(sectionId, useCache, new URL(url.href)),
    ])
  );
  return new Map(entries);
}

function handlePopState() {
  const host = document.querySelector('kb-pdp-product-switch');
  if (!(host instanceof KbPdpProductSwitch)) return;
  host.swapTo(window.location.href, { push: false });
}

class KbPdpProductSwitch extends HTMLElement {
  constructor() {
    super();
    this.selectors = {
      option: '[data-kb-sibling-option]',
      details: '[data-kb-sibling-details]',
      trigger: '[data-kb-sibling-trigger]',
      warmTarget: '[data-kb-sibling-details], [data-kb-sibling-option]',
      swapTarget: '[data-kb-swap-target]',
      productTitle: '.kb-pdp-buy-box__title',
      templateSection: '[id^="shopify-section-template--"]',
      canonical: 'link[rel="canonical"]',
    };
    this.attributeNames = {
      busy: 'aria-busy',
      current: 'aria-current',
    };
    this.settings = {
      reviewsGlobal: 'yotpoWidgetsContainer',
      historyStateKey: 'kbPdpProductSwitch',
      transitionType: 'page-navigation',
      dataLayerEvent: 'kb_pdp_type_switch',
    };
    this.swapping = false;
    this.warmed = false;
    /** @type {AbortController|null} */
    this.abortController = null;
  }

  connectedCallback() {
    const section = this.sectionRoot();
    if (!section) return;

    this.abortController = new AbortController();
    const { signal } = this.abortController;
    section.addEventListener('click', this.handleClick, { signal });
    section.addEventListener('pointerover', this.handleIntent, { signal });
    section.addEventListener('focusin', this.handleIntent, { signal });
    section.addEventListener('toggle', this.handleIntent, { signal, capture: true });
    this.bindHistory();
  }

  disconnectedCallback() {
    if (this.abortController) this.abortController.abort();
  }

  bindHistory() {
    if (popstateBound) return;
    popstateBound = true;
    window.addEventListener('popstate', handlePopState);
  }

  /** @param {Event} event */
  handleClick = (event) => {
    if (!(event instanceof MouseEvent)) return;
    if (!(event.target instanceof Element)) return;
    const option = /** @type {HTMLAnchorElement|null} */ (event.target.closest(this.selectors.option));
    if (!option) return;

    if (option.hasAttribute(this.attributeNames.current)) {
      event.preventDefault();
      this.close();
      return;
    }
    if (this.swapping) {
      event.preventDefault();
      return;
    }
    if (!this.canIntercept(event)) return;

    event.preventDefault();
    this.close();
    this.swapTo(option.href, { push: true });
  };

  /**
   * @param {Event} event
   */
  handleIntent = (event) => {
    if (this.warmed || this.designMode()) return;
    if (!(event.target instanceof Element)) return;
    if (!event.target.closest(this.selectors.warmTarget)) return;

    const sectionId = this.mainSectionId();
    if (!sectionId) return;

    this.warmed = true;
    this.options().forEach((option) => {
      if (option.hasAttribute(this.attributeNames.current)) return;
      sectionRenderer.getSectionHTML(sectionId, true, new URL(option.href));
    });
  };

  /**
   * @param {MouseEvent} event
   * @returns {boolean}
   */
  canIntercept(event) {
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return false;
    if (typeof event.button === 'number' && event.button !== 0) return false;
    return !this.designMode();
  }

  /** @returns {boolean} */
  designMode() {
    const shopify = /** @type {{ designMode?: boolean }|undefined} */ (/** @type {any} */ (window).Shopify);
    return Boolean(shopify && shopify.designMode);
  }

  /** @returns {string} */
  mainSectionId() {
    return this.dataset.kbSectionId || '';
  }

  /** @returns {HTMLElement|null} */
  sectionRoot() {
    const sectionId = this.mainSectionId();
    if (!sectionId) return null;
    return document.getElementById(buildSectionSelector(sectionId));
  }

  /** @returns {HTMLAnchorElement[]} */
  options() {
    const section = this.sectionRoot();
    if (!section) return [];
    return /** @type {HTMLAnchorElement[]} */ (Array.from(section.querySelectorAll(this.selectors.option)));
  }

  /**
   * @returns {string[]}
   */
  swapSectionIds() {
    const ids = [];
    const mainSectionId = this.mainSectionId();
    if (mainSectionId) ids.push(mainSectionId);

    document.querySelectorAll(this.selectors.templateSection).forEach((element) => {
      if (!element.id) return;
      const sectionId = normalizeSectionId(element.id);
      if (sectionId && !ids.includes(sectionId)) ids.push(sectionId);
    });

    return ids;
  }

  /**
   * @param {string} href
   * @param {KbSwapOptions} options
   */
  async swapTo(href, options) {
    const mainSectionId = this.mainSectionId();
    if (!mainSectionId) return;

    const url = new URL(href, window.location.href);
    this.swapping = true;
    this.setBusy(true);

    try {
      const sectionIds = this.swapSectionIds();
      const unknownIds = sectionIds.filter((sectionId) => !renderedSectionHTML.has(sectionId));
      const [nextHTML, currentHTML] = await Promise.all([
        fetchSectionHTML(sectionIds, url, !this.designMode()),
        fetchSectionHTML(unknownIds, new URL(window.location.href), false),
      ]);
      currentHTML.forEach((html, sectionId) => renderedSectionHTML.set(sectionId, html));

      /** @type {KbSectionReplacement[]} */
      const replacements = [];
      let mainResolved = false;
      for (const sectionId of sectionIds) {
        const html = nextHTML.get(sectionId);
        const replacement = this.buildReplacement(sectionId, html);
        if (!replacement) continue;
        if (sectionId === mainSectionId) mainResolved = true;
        if (html === renderedSectionHTML.get(sectionId)) continue;
        replacements.push(replacement);
      }

      if (!mainResolved) {
        window.location.assign(url.href);
        return;
      }

      if (!replacements.length) return;

      if (options.push) {
        window.history.pushState({ [this.settings.historyStateKey]: true }, '', url.href);
      }
      this.updateCanonical(url.origin + url.pathname);

      await withViewTransition(() => {
        replacements.forEach((replacement) => replacement.current.replaceWith(replacement.next));
        this.applyDocumentTitle();
        this.reinitialiseReviews();
        this.focusTrigger();
      }, this.settings.transitionType);
      nextHTML.forEach((html, sectionId) => renderedSectionHTML.set(sectionId, html));
      this.publishSwitch(url);
    } catch (error) {
      window.location.assign(url.href);
    } finally {
      this.swapping = false;
      this.setBusy(false);
      this.warmed = false;
    }
  }

  /**
   * @param {string} sectionId
   * @param {string} html
   * @returns {KbSectionReplacement|null}
   */
  buildReplacement(sectionId, html) {
    const wrapperId = buildSectionSelector(sectionId);
    const currentSection = document.getElementById(wrapperId);
    if (!currentSection) return null;
    const fragment = new DOMParser().parseFromString(html, 'text/html');
    const nextSection = fragment.getElementById(wrapperId);
    if (!nextSection) return null;

    const currentTarget = currentSection.querySelector(this.selectors.swapTarget);
    const nextTarget = nextSection.querySelector(this.selectors.swapTarget);
    if (currentTarget && nextTarget) {
      return { sectionId, current: currentTarget, next: nextTarget };
    }
    return { sectionId, current: currentSection, next: nextSection };
  }

  applyDocumentTitle() {
    const section = this.sectionRoot();
    const current = section
      ? section.querySelector(`${this.selectors.option}[${this.attributeNames.current}]`)
      : null;
    if (!(current instanceof HTMLElement)) return;
    const title = current.dataset.kbDocumentTitle;
    if (title) document.title = title;
  }

  /** @param {string} href */
  updateCanonical(href) {
    const link = document.head.querySelector(this.selectors.canonical);
    if (link instanceof HTMLLinkElement) link.href = href;
  }

  /**
   * @param {URL} url
   */
  publishSwitch(url) {
    const dataLayer = /** @type {unknown[]|undefined} */ (/** @type {any} */ (window).dataLayer);
    if (!Array.isArray(dataLayer)) return;
    const heading = document.querySelector(this.selectors.productTitle);
    dataLayer.push({
      event: this.settings.dataLayerEvent,
      product_title: heading && heading.textContent ? heading.textContent.trim() : '',
      product_url: url.origin + url.pathname,
    });
  }

  reinitialiseReviews() {
    const container = /** @type {{ initWidgets?: () => void }|undefined} */ (
      /** @type {any} */ (window)[this.settings.reviewsGlobal]
    );
    if (container && typeof container.initWidgets === 'function') container.initWidgets();
  }

  focusTrigger() {
    const section = this.sectionRoot();
    if (!section) return;
    const trigger =
      section.querySelector(this.selectors.trigger) ||
      section.querySelector(`${this.selectors.option}[${this.attributeNames.current}]`);
    if (trigger instanceof HTMLElement) trigger.focus({ preventScroll: true });
  }

  /** @param {boolean} busy */
  setBusy(busy) {
    const section = this.sectionRoot();
    if (!section) return;
    if (busy) section.setAttribute(this.attributeNames.busy, 'true');
    else section.removeAttribute(this.attributeNames.busy);
  }

  close() {
    const section = this.sectionRoot();
    const details = section ? section.querySelector(this.selectors.details) : null;
    if (details instanceof HTMLDetailsElement) details.open = false;
  }
}

if (!customElements.get('kb-pdp-product-switch')) {
  customElements.define('kb-pdp-product-switch', KbPdpProductSwitch);
}

export { KbPdpProductSwitch };
